import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Server-synced personal token lists.
 *
 * Both tables are RLS-scoped to auth.uid(), so every read/write here only ever
 * touches the signed-in user's own rows. Anonymous visitors get empty lists and
 * never write anything — no local-storage fallback by design.
 */

const RECENT_LIMIT = 20;

export function useWatchlist() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["user-watchlist", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_watchlist")
        .select("token_symbol, added_at")
        .order("added_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

  const symbols = new Set((query.data ?? []).map((r) => r.token_symbol.toUpperCase()));

  const toggle = useMutation({
    mutationFn: async (rawSymbol: string) => {
      if (!user) throw new Error("not signed in");
      const symbol = rawSymbol.toUpperCase();
      if (symbols.has(symbol)) {
        const { error } = await supabase
          .from("user_watchlist")
          .delete()
          .eq("user_id", user.id)
          .eq("token_symbol", symbol);
        if (error) throw error;
        return { symbol, pinned: false };
      }
      const { error } = await supabase
        .from("user_watchlist")
        .insert({ user_id: user.id, token_symbol: symbol });
      if (error) throw error;
      return { symbol, pinned: true };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-watchlist", user?.id] }),
  });

  return {
    items: query.data ?? [],
    symbols,
    isLoading: query.isLoading,
    isPinned: (s: string) => symbols.has(s.toUpperCase()),
    toggle: (s: string) => toggle.mutateAsync(s),
    isToggling: toggle.isPending,
  };
}

export function useRecentlyViewed() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["user-recently-viewed", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_recently_viewed")
        .select("token_symbol, viewed_at")
        .order("viewed_at", { ascending: false })
        .limit(RECENT_LIMIT);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * Records that a signed-in user opened a token view, keeping only their most
 * recent 20 rows. No-op for anonymous visitors.
 */
export async function logRecentlyViewed(userId: string | undefined, rawSymbol: string) {
  if (!userId || !rawSymbol) return;
  const token_symbol = rawSymbol.toUpperCase().trim();
  if (!token_symbol) return;

  const { error } = await supabase
    .from("user_recently_viewed")
    .upsert(
      { user_id: userId, token_symbol, viewed_at: new Date().toISOString() },
      { onConflict: "user_id,token_symbol" },
    );
  if (error) {
    console.warn("recently-viewed write failed", error.message);
    return;
  }

  // Trim anything older than the most recent 20.
  const { data: rows } = await supabase
    .from("user_recently_viewed")
    .select("id")
    .order("viewed_at", { ascending: false });
  const stale = (rows ?? []).slice(RECENT_LIMIT).map((r) => r.id);
  if (stale.length > 0) {
    await supabase.from("user_recently_viewed").delete().in("id", stale);
  }
}
